import { useEffect, useState } from "react";
import pillboxService from "@/services/pillbox.service";
import { Member } from "@/types/member";

/**
 * Filters `members` down to those with at least one active pillbox item.
 * Runs as soon as `members` is available so the check is (usually) already
 * resolved by the time a caller needs the result — e.g. before opening a
 * drawer whose height should reflect the final list from its first frame,
 * rather than opening early and resizing once the check completes.
 */
export function useMembersWithMedicines(members: Member[]) {
  const [eligibleIds, setEligibleIds] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);
  const memberIdsKey = members.map((m) => m.id).join(",");

  useEffect(() => {
    if (members.length === 0) {
      setEligibleIds(new Set());
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      members.map((member) =>
        pillboxService
          .getLedger(member.id, "ACTIVE")
          .then((items) => [member.id, items.length > 0] as const)
          .catch(() => [member.id, false] as const)
      )
    ).then((results) => {
      if (cancelled) return;
      setEligibleIds(
        new Set(results.filter(([, has]) => has).map(([id]) => id))
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberIdsKey]);

  const eligibleMembers =
    eligibleIds !== null ? members.filter((m) => eligibleIds.has(m.id)) : [];

  return { eligibleMembers, loading, ready: eligibleIds !== null };
}
